import { memo } from 'react'
import { colors } from 'theme/colors'
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  Legend,
  LabelList,
  Label,
} from 'recharts'
import { Box } from '@mui/material'
import { DEBOUNCE_DELAY_DEFAULT, INACTIVE_BAR_BACKGROUND_COLOR } from './consts'
import { SECTION_TITLES } from 'ui/clinicianScreens/Patient/Daily/consts'

const BarChart = ({
  title,
  data,
  legendItem,
  legend,
  height = 200,
  showBarLabels = false,
  barSize = 10,
  hideVerticalGridLines = false,
  hideGridLines = false,
  hideXAxis = false,
  hideYAxis = false,
  hideXAxisTickLine = false,
  hideTopXAxisGridLines = false,
  xAxisLabel,
  xAxisInterval = 11,
  yAxisUnit,
  yAxisMax,
  tooltip,
  referenceLineValue,
  referenceLineLabel,
  secondaryBarLabels,
  isAnimationActive,
  margin = { top: 0, right: 16, left: 10, bottom: 5 },
  barRadius = [0, 0, 0, 0],
  chartId = 'bar-chart',
  showInactiveBackground,
  showOverflowingBars,
  containerStyles,
  ...props
}) => {
  const STYLES = {
    chartContainer: {
      width: '100%',
      height,
      '& .recharts-surface': showOverflowingBars
        ? {
            overflow: 'visible',
          }
        : {},
      '& .recharts-cartesian-grid-horizontal line:first-of-type': {
        display: hideTopXAxisGridLines ? 'none' : 'block',
      },
      '& .recharts-yAxis line:first-of-type': {
        display: 'none',
      },
      '& .recharts-legend-item-text': {
        color: `${colors.GREY[700]} !important`, // overriding js
        fontSize: '12px',
        verticalAlign: 'middle',
      },
      '& .recharts-legend-wrapper': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: title ? 'space-between' : 'end',
        width: '100%',
        ...(title && {
          '&::before': {
            content: `"Time with ${title}"`,
            color: colors.GREY[700],
            marginLeft: '3.5rem',
          },
        }),
      },
      '@media print': {
        '& .recharts-legend-wrapper::before': {
          marginLeft: '4.75cm',
        },
      },
    },
  }

  // Callers may pass a stable `dataKey` so the series identity in the chart
  // data stays independent of the (potentially localized) legend text.
  const legendSeriesKey = legendItem?.dataKey ?? legendItem?.name

  const getBarBackground = (props) => {
    const { x, y, width, height, payload } = props
    const backgroundColor = payload.isNull
      ? INACTIVE_BAR_BACKGROUND_COLOR
      : 'transparent'

    return (
      <rect x={x} y={y} width={width} height={height} fill={backgroundColor} />
    )
  }

  const getBarLine = (props) => {
    const { x, y, width, payload } = props
    const showLine = payload[SECTION_TITLES.medication]
    return (
      <rect
        {...{
          x,
          y,
          width,
          height: 500,
          fill: showLine ? colors.PURPLE[100] : 'transparent',
          className: showLine ? 'barline' : '',
        }}
      />
    )
  }

  return (
    <Box
      sx={{ ...STYLES.chartContainer, ...containerStyles }}
      data-chart-id={chartId}
    >
      <ResponsiveContainer
        debounce={DEBOUNCE_DELAY_DEFAULT}
        width="100%"
        height={height}
      >
        <RechartsBarChart
          {...{
            width: 500,
            height,
            data,
            margin,
            ...props,
          }}
        >
          <CartesianGrid
            strokeDasharray="0"
            stroke={colors.TREMOR[100]}
            horizontal={!hideGridLines}
            vertical={!hideGridLines && !hideVerticalGridLines}
          />
          <XAxis
            dataKey="name"
            interval={xAxisInterval}
            tickMargin={8}
            fontSize={10}
            axisLine={{ stroke: colors.GREY[400] }}
            tickLine={
              hideXAxisTickLine ? false : { stroke: colors.TREMOR[300] }
            }
            hide={hideXAxis}
          >
            <Label
              value={xAxisLabel}
              offset={showBarLabels ? 25 : 0}
              position="bottom"
            />
          </XAxis>
          <YAxis
            unit={yAxisUnit}
            tickMargin={8}
            fontSize={10}
            axisLine={{ stroke: colors.TREMOR[100] }}
            tickLine={{ stroke: colors.TREMOR[100] }}
            domain={[0, yAxisMax]}
            hide={hideYAxis}
          />
          {tooltip && (
            <Tooltip cursor={{ fill: colors.PRIMARY[200] }} content={tooltip} />
          )}
          {legend && (
            <Legend
              verticalAlign="top"
              align="right"
              iconType="square"
              iconSize={10}
              wrapperStyle={{ top: -8, fontSize: 10 }}
            />
          )}
          {legendItem && (
            <Bar
              {...{
                key: legendSeriesKey,
                dataKey: legendSeriesKey,
                // Recharts falls back to dataKey for the legend text, so only
                // set `name` when the displayed label differs from the key.
                name: legendItem.name,
                fill: legendItem.color,
                barSize,
                isAnimationActive,
                radius: barRadius,
                background: showInactiveBackground
                  ? getBarBackground
                  : showOverflowingBars
                  ? getBarLine
                  : null,
              }}
            >
              {showBarLabels && (
                <>
                  <LabelList dataKey={legendSeriesKey} position="top" />
                  {secondaryBarLabels}
                </>
              )}
            </Bar>
          )}
          <ReferenceLine
            label={referenceLineLabel}
            y={referenceLineValue}
            stroke={colors.LIME[700]}
            strokeWidth={3}
          />
        </RechartsBarChart>
      </ResponsiveContainer>
    </Box>
  )
}

export default memo(BarChart)
